import { useEffect, useState } from "react";
import { playerService } from "../../services/api";
import SideHeader from "../player/PlayerMatches/SideHeader";
import PerformanceSummary from "../player/PlayerMatches/PerformanceSummary";
import DetailedStatistics from "../player/PlayerMatches/DetailedStatistics";
import { isGoalkeeperPlayer } from "../player/PlayerMatches/matchStatGroups";
import MatchLineup from "../player/MatchLineup/MatchLineup";
import { ratingTier10, formatRating10 } from "../totw/totwUtils";

// Normalise SportMonks codes / developer_names to a consistent, JS-friendly key.
// Example: "MINUTES_PLAYED" -> "minutes_played", "minutes-played" -> "minutes_played",
//          "YELLOWCARDS" -> "yellowcards".
const normalizeSmCode = (raw) => {
  if (!raw) {
    return "";
  }

  let s = String(raw).toLowerCase();
  s = s.replace(/[^a-z0-9]+/g, "_"); // replace non-alphanumerics with underscores
  s = s.replace(/^_+|_+$/g, ""); // trim leading/trailing underscores
  s = s.replace(/_+/g, "_"); // collapse multiple underscores
  return s;
};

// Map normalised SM codes to the canonical keys used in the UI.
// Keys here must be the output of normalizeSmCode on either developer_name or type_code.
const SM_STAT_CODE_TO_KEY = {
  // Overall / summary
  minutes_played: "minutes_played",
  minutes: "minutes_played",
  rating: "rating",

  // Goals / attacking
  goals: "goals",

  // Shooting
  shots: "shots",
  shots_total: "shots",
  shots_on_target: "shots_on_target",

  // Passing
  passes: "passes",
  accurate_passes: "passes_accuracy",
  successful_passes: "passes_accuracy", // absolute count of accurate passes
  key_passes: "key_passes",

  // Long balls
  long_balls: "long_balls",
  long_balls_won: "long_balls_accuracy", // successful long balls

  // Possession / control
  touches: "touches",
  possession_lost: "poss_losts",

  // Discipline
  fouls: "fouls",
  yellowcards: "yellow_cards",
  redcards: "red_cards",
};

const indexSmTypes = (types) => {
  if (!Array.isArray(types) || types.length === 0) {
    return { byCode: {}, byDeveloperName: {} };
  }

  const byCode = {};
  const byDeveloperName = {};

  types.forEach((item) => {
    const code = item?.code ? String(item.code).toLowerCase() : null;
    const dev = item?.developer_name
      ? String(item.developer_name).toLowerCase()
      : null;

    if (code && !byCode[code]) {
      byCode[code] = item;
    }
    if (dev && !byDeveloperName[dev]) {
      byDeveloperName[dev] = item;
    }
  });

  return { byCode, byDeveloperName };
};

const buildImageUrl = (hashOrUrl) => {
  if (!hashOrUrl) {
    return null;
  }

  const value = String(hashOrUrl);

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  return `https://images.sportdevs.com/${value}.png`;
};

const normalizeSportmonksStats = (statsArray, smTypesIndex) => {
  const normalized = {};
  const meta = {};

  if (!Array.isArray(statsArray)) {
    return { stats: normalized, meta };
  }

  statsArray.forEach((row) => {
    const rawCodeSource = row?.developer_name || row?.type_code || "";
    const normalisedCode = normalizeSmCode(rawCodeSource);
    if (!normalisedCode) {
      return;
    }

    const typeMeta =
      smTypesIndex?.byCode?.[normalisedCode] ||
      smTypesIndex?.byDeveloperName?.[normalisedCode] ||
      null;

    // If we know how to map this code to a UI key, use that; otherwise just use
    // the normalised code itself so it can at least appear in the "Other" section.
    const key = SM_STAT_CODE_TO_KEY[normalisedCode] || normalisedCode;

    const value = row?.value;
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return;
    }

    const numericValue = Number(value);
    normalized[key] = numericValue;

    meta[key] = {
      code: normalisedCode,
      developerName: row?.developer_name || null,
      typeId: row?.type_id,
      name:
        typeMeta?.name ||
        row?.type_name ||
        (row?.developer_name ? row.developer_name : normalisedCode),
      statGroup: typeMeta?.stat_group || null,
      modelType: typeMeta?.model_type || null,
    };
  });

  // Derive shot_off_target where possible
  if (
    normalized.shot_off_target === undefined &&
    normalized.shots !== undefined &&
    normalized.shots_on_target !== undefined
  ) {
    normalized.shot_off_target = Math.max(
      0,
      normalized.shots - normalized.shots_on_target
    );
  }

  return { stats: normalized, meta };
};

const normalizeMatchEntry = (matchEntry, smTypesIndex) => {
  if (!matchEntry || !matchEntry.source) {
    return matchEntry;
  }

  const source = String(matchEntry.source).toLowerCase();

  if (source !== "sportmonks") {
    // TheSports entries are already in the correct shape for the UI.
    return matchEntry;
  }

  const { stats: normalizedStats, meta } = normalizeSportmonksStats(
    matchEntry.statistics,
    smTypesIndex
  );

  return {
    ...matchEntry,
    statistics: {
      ...(matchEntry.statistics && !Array.isArray(matchEntry.statistics)
        ? matchEntry.statistics
        : {}),
      ...normalizedStats,
    },
    smStatisticsMeta: meta,
  };
};

const PlayerMatches = ({ playerData }) => {
  const [matchHistory, setMatchHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  // Heatmap state kept for potential future re-enable of the HeatMap section.
  // Currently unused, so kept minimal to avoid linter warnings.
  // eslint-disable-next-line no-unused-vars
  const [matchHeatmapData, setMatchHeatmapData] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [matchHeatmapLoading, setMatchHeatmapLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [matchHeatmapError, setMatchHeatmapError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  // eslint-disable-next-line no-unused-vars
  const [smTypesIndex, setSmTypesIndex] = useState(null);

  // Get wyscout_id from playerData
  const wyscoutId =
    playerData?.identifiers?.wyscoutId ??
    playerData?.player?.id ??
    playerData?.id ??
    null;

  useEffect(() => {
    if (!wyscoutId) {
      return;
    }

    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1. Fetch SM types first for dynamic mapping
        const typesResponse = await playerService.getSmTypes();

        if (!typesResponse.success) {
          throw new Error(
            typesResponse.error || "Failed to load SportMonks types metadata"
          );
        }

        const index = indexSmTypes(typesResponse.data || []);
        if (!isMounted) return;
        setSmTypesIndex(index);

        // 2. Fetch match history
        const historyResponse = await playerService.getPlayerMatchHistory(
          wyscoutId,
          20
        );

        if (!historyResponse.success) {
          throw new Error(
            historyResponse.error ||
              "Something went wrong while loading match history"
          );
        }

        const rawMatches = historyResponse.data || [];
        const normalizedMatches = rawMatches.map((entry) =>
          normalizeMatchEntry(entry, index)
        );

        if (!isMounted) return;

        setMatchHistory(normalizedMatches);
        setCurrentPage(1);

        if (normalizedMatches.length > 0) {
          setSelectedMatch(normalizedMatches[0]);
        } else {
          setSelectedMatch(null);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error("Error fetching match history:", err);
        setError(
          err?.message || "Something went wrong while loading match history"
        );
        setMatchHistory([]);
        setSelectedMatch(null);
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [wyscoutId]);

  // Fetch heatmap when a match is selected
  useEffect(() => {
    if (!wyscoutId || !selectedMatch?.match?.id) {
      setMatchHeatmapData([]);
      setMatchHeatmapLoading(false);
      setMatchHeatmapError(null);
      return;
    }

    let isMounted = true;

    const fetchHeatmap = async () => {
      setMatchHeatmapLoading(true);
      setMatchHeatmapError(null);

      try {
        const response = await playerService.getPlayerMatchHeatmap(
          selectedMatch.match.id,
          wyscoutId
        );

        if (!isMounted) return;

        if (response.success) {
          // Heatmap aggregation is currently disabled; keep raw payload in state
          // if needed in the future, without breaking the flow.
          const payload = response.data;
          const entry = Array.isArray(payload)
            ? payload[0]
            : Array.isArray(payload?.results)
            ? payload.results[0]
            : payload;

          console.log("entry", entry);
          setMatchHeatmapData(entry?.heatmap?.heatmaps || []);
        } else {
          setMatchHeatmapData([]);
          setMatchHeatmapError(
            response.error || "Unable to load match heatmap."
          );
        }
      } catch (error) {
        if (isMounted) {
                  console.error("Error fetching match heatmap:", error);
          setMatchHeatmapData([]);
          setMatchHeatmapError("Unable to load match heatmap.");
        }
      } finally {
        if (isMounted) {
          setMatchHeatmapLoading(false);
        }
      }
    };

    fetchHeatmap();

    return () => {
      isMounted = false;
    };
  }, [wyscoutId, selectedMatch?.match?.id]);

  // Pagination calculations
  const totalMatches = matchHistory.length;
  const totalPages = Math.ceil(totalMatches / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMatches = matchHistory.slice(startIndex, endIndex);

  // Group paginated matches by tournament
  const groupedMatches = paginatedMatches.reduce((acc, matchData) => {
    const tournamentName = matchData.match?.league_name || matchData.match?.name || "Match";
    if (!acc[tournamentName]) {
      acc[tournamentName] = [];
    }
    acc[tournamentName].push(matchData);
    return acc;
  }, {});

  // Handlers for pagination
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Update selected match when page changes
  useEffect(() => {
    if (paginatedMatches.length > 0) {
      // Check if current selected match is on this page
      const isSelectedOnPage = paginatedMatches.some(
        (match) => match.match?.id === selectedMatch?.match?.id
      );

      // If not on page, select the first match of the current page
      if (!isSelectedOnPage) {
        setSelectedMatch(paginatedMatches[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    } catch (error) {
      return "";
    }
  };

  const getPlayerTeam = (matchData) => {
    const playerTeamId = String(matchData?.player_team_id);
    const match = matchData.match;

    if (String(match?.home_team_id) === playerTeamId) {
      return {
        id: String(match?.home_team_id),
        name: match?.home_team_name,
        hash_image: match?.home_team_hash_image,
        score: match?.score?.home || 0,
      };
    } else {
      return {
        id: String(match?.away_team_id),
        name: match?.away_team_name,
        hash_image: match?.away_team_hash_image,
        score: match?.score?.away || 0,
      };
    }
  };

  const getOpponent = (matchData) => {
    const opponent = matchData?.opponent;
    const playerTeamId = String(matchData?.player_team_id);
    const match = matchData.match;

    const opponentScore =
      String(match?.home_team_id) === playerTeamId
        ? match?.score?.away || 0
        : match?.score?.home || 0;

    return {
      ...opponent,
      score: opponentScore,
    };
  };

  const handleMatchSelect = (matchData) => {
    setSelectedMatch(matchData);

    if (!Array.isArray(matchHistory) || matchHistory.length === 0) {
      return;
    }

    const matchIndex = matchHistory.findIndex(
      (item) => item.match?.id === matchData.match?.id
    );

    if (matchIndex === -1) {
      return;
    }

    const pageNumber = Math.floor(matchIndex / itemsPerPage) + 1;
    if (pageNumber !== currentPage) {
      setCurrentPage(pageNumber);
    }
  };

  const renderMatchCard = (
    matchData,
    variant = "desktop",
    fallbackTournamentName = "Tournament"
  ) => {
    const isSelected = selectedMatch?.match?.id === matchData.match?.id;
    const playerTeam = getPlayerTeam(matchData);
    const opponent = getOpponent(matchData);
    // Part D rating unification: ONE shared tier scale + one-decimal format
    // (totwUtils). Null/invalid rating -> no chip at all.
    const ratingText = formatRating10(matchData.statistics?.rating);
    const ratingTierColors = ratingText
      ? ratingTier10(matchData.statistics?.rating)
      : null;

    const teamLogo = buildImageUrl(playerTeam.hash_image);
    const opponentLogo = buildImageUrl(opponent.hash_image);

    const baseClass =
      variant === "mobile"
        ? "rounded-xl p-4 cursor-pointer transition-all min-w-[240px] sm:min-w-[260px] shrink-0 snap-start"
        : "rounded-xl p-4 cursor-pointer transition-all";

    const ratingContainerClass =
      variant === "mobile"
        ? "flex h-fit w-full sm:w-[15%] sm:mt-3 sm:justify-end"
        : "flex h-fit mt-3 w-[15%]";

    const competitionName =
      matchData.match?.league_name || matchData.match?.name || fallbackTournamentName || "Match";

    return (
      <div
        key={matchData.match?.id || `${variant}-match-${Math.random()}`}
        onClick={() => handleMatchSelect(matchData)}
        className={`${baseClass} ${
          isSelected
            ? "border-2 border-[#50C8E9] bg-[#EDF3FB]"
            : "border border-[#E8ECF4] bg-white"
        }`}
      >
        <div className="flex items-center justify-between gap-2 mb-3 border-b border-[#E5E5E5] pb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-[#1A212F] font-medium text-sm">
              {competitionName}
            </span>
          </div>
          <span className="text-[#6B7280] text-xs">
            {formatDate(matchData.match?.kickoff_at)}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start sm:items-center">
          <div className="space-y-2 w-full sm:w-[85%]">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-1">
                {teamLogo && (
                  <img
                    src={teamLogo}
                    alt={playerTeam.name}
                    className="w-8 h-8 object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <span className="text-[#6B7280] font-medium text-sm">
                  {playerTeam.name || "Team"}
                </span>
              </div>
              <span className="text-[#1A212F] font-bold text-base">
                {playerTeam.score}
              </span>
            </div>

            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-1">
                {opponentLogo ? (
                  <img
                    src={opponentLogo}
                    alt={opponent.name}
                    className="w-8 h-8 object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-8 h-8 bg-gray-200 rounded-full" />
                )}
                <span className="text-[#6B7280] font-medium text-sm">
                  {opponent.name || "Opponent"}
                </span>
              </div>
              <span className="text-[#1A212F] font-bold text-base">
                {opponent.score}
              </span>
            </div>
          </div>

          {ratingText && (
            <div className={ratingContainerClass}>
              <div
                className="px-3 py-1 rounded-md font-extrabold text-sm"
                style={{
                  backgroundColor: ratingTierColors.bg,
                  color: ratingTierColors.fg,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {ratingText}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-[20px] border border-[#D6DBEA] p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
        </div>
      </div>
    );
  }

  if (error) {
    const isNoSportsId = error.toLowerCase().includes("player has no thesports id");

    return (
      <div className="bg-white rounded-[20px] border border-[#D6DBEA] p-6">
        <div className="text-center py-12">
          <p className={isNoSportsId ? "text-gray-500" : "text-red-600"}>
            {isNoSportsId ? "Match History is not available for this player" : error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 h-full">
      {/* Left Side - Match List (30%) */}
      <div className="w-full lg:w-[30%] flex flex-col">
        <div className="bg-white rounded-[20px] border border-[#D6DBEA] overflow-hidden flex flex-col">
          {/* Header with Pagination */}
          <div className="bg-[#F0F0F2] border-b border-[#D6DBEA]/30 rounded-t-[20px] px-4 py-3 sm:px-6 sm:py-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h3 className="text-[#1A212F] font-bold text-base">
                Match History
              </h3>

              {/* Pagination Controls */}
              {totalMatches > itemsPerPage && (
                <div className="hidden lg:flex items-center gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`p-1.5 rounded-lg transition-colors ${
                      currentPage === 1
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-white text-[#1A212F] hover:bg-gray-100"
                    }`}
                    aria-label="Previous page"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.5 15L7.5 10L12.5 5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <span className="text-[#6B7280] text-sm font-medium min-w-[60px] text-center">
                    {currentPage} / {totalPages}
                  </span>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`p-1.5 rounded-lg transition-colors ${
                      currentPage === totalPages
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-white text-[#1A212F] hover:bg-gray-100"
                    }`}
                    aria-label="Next page"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.5 15L12.5 10L7.5 5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Content - No scrolling, fit content */}
          <div className="p-4 sm:p-6">
            {Object.keys(groupedMatches).length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No match data available</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="lg:hidden">
                  <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
                    {matchHistory.map((matchData) =>
                      renderMatchCard(matchData, "mobile")
                    )}
                  </div>
                </div>

                <div className="hidden lg:flex flex-col space-y-6">
                  {Object.entries(groupedMatches).map(
                    ([tournamentName, tournamentMatches]) => (
                      <div key={tournamentName}>
                        {/* Tournament Section Header */}
                        <div className="mb-4">
                          <h4 className="text-[#1A212F] font-bold text-sm">
                            {tournamentName}
                          </h4>
                        </div>

                        {/* Matches List */}
                        <div className="space-y-3">
                          {tournamentMatches.map((matchData) =>
                            renderMatchCard(
                              matchData,
                              "desktop",
                              tournamentName
                            )
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Match Details (70%) */}
      <div className="w-full lg:w-[70%] flex flex-col mt-6 lg:mt-0">
        <div className="bg-white rounded-[20px] border border-[#D6DBEA] h-full overflow-hidden flex flex-col">
          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            {!selectedMatch ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Select a match to view details</p>
              </div>
            ) : (
              <div>
                {/* Match Header */}

                <SideHeader
                  selectedMatch={selectedMatch}
                  getPlayerTeam={getPlayerTeam}
                  getOpponent={getOpponent}
                  formatDate={formatDate}
                />

                {/* Main Content */}
                <div className="p-4 sm:p-6 space-y-6">
                  {/* Performance Summary */}
                  <PerformanceSummary selectedMatch={selectedMatch} />

                  {/* Detailed Statistics */}
                  <DetailedStatistics
                    selectedMatch={selectedMatch}
                    isGoalkeeper={isGoalkeeperPlayer(playerData)}
                  />

                  {/* Match Lineup — directly below the Detailed Statistics cards.
                      Highlight matches on the lineup players' platform_player_id
                      (profile detail ships it as playerData.platform_id — the
                      WSPlayer route id never appears in the lineup payload). */}
                  <MatchLineup
                    matchId={selectedMatch.match?.id}
                    highlightPlayerId={
                      playerData?.platform_id ??
                      playerData?.player?.platform_id ??
                      wyscoutId
                    }
                  />

                  {/* Heat Map */}
                  {/* <div>
                    <div className="flex items-center gap-2 mb-4">
                      <svg
                        width="18"
                        height="20"
                        viewBox="0 0 18 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.5 8.33331C16.5 14.1666 9 19.1666 9 19.1666C9 19.1666 1.5 14.1666 1.5 8.33331C1.5 6.34419 2.29018 4.43653 3.6967 3.03001C5.10322 1.62349 7.01088 0.833313 9 0.833313C10.9891 0.833313 12.8968 1.62349 14.3033 3.03001C15.7098 4.43653 16.5 6.34419 16.5 8.33331Z"
                          stroke="#2F6AF5"
                          stroke-width="1.65"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M9 10.8333C10.3807 10.8333 11.5 9.71403 11.5 8.33331C11.5 6.9526 10.3807 5.83331 9 5.83331C7.61929 5.83331 6.5 6.9526 6.5 8.33331C6.5 9.71403 7.61929 10.8333 9 10.8333Z"
                          stroke="#2F6AF5"
                          stroke-width="1.65"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>

                      <h3 className="text-[#1A212F] font-bold text-lg">
                        Heat Map
                      </h3>
                    </div>

                    <div className="bg-white border border-[#E5E6E8] rounded-xl p-4 sm:p-6">
                      <HeatMap
                        heatmapData={matchHeatmapData}
                        isLoading={matchHeatmapLoading}
                        error={matchHeatmapError}
                      />
                    </div>
                  </div> */}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerMatches;
